import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Send, Bot, User } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AICommand = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        {
          message: input,
          conversation_id: conversationId
        },
        { headers: getAuthHeaders() }
      );

      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to send message');
      console.error('Chat error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-6rem)] flex flex-col" data-testid="ai-command-page">
        {/* Header with Examples */}
        <div className="mb-4">
          <h1 className="text-4xl font-bold text-white mb-2">TALK TO LEE</h1>
          <p className="text-muted-foreground mb-3">Give commands • Execute directives • Get intelligence</p>
          
          {/* Example Commands */}
          <div className="glass-card p-3 border border-primary/20 bg-primary/5">
            <p className="text-xs uppercase tracking-widest text-primary mb-2">💡 Example Commands:</p>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setInput("Execute full analysis on my invention idea")}
                className="px-3 py-1 bg-secondary/50 hover:bg-secondary text-xs text-muted-foreground hover:text-white transition-colors"
              >
                Analyze invention
              </button>
              <button 
                onClick={() => setInput("What's my most important task?")}
                className="px-3 py-1 bg-secondary/50 hover:bg-secondary text-xs text-muted-foreground hover:text-white transition-colors"
              >
                Most important task
              </button>
              <button 
                onClick={() => setInput("Create my 90-day execution plan")}
                className="px-3 py-1 bg-secondary/50 hover:bg-secondary text-xs text-muted-foreground hover:text-white transition-colors"
              >
                90-day plan
              </button>
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div className="flex-1 glass-card border border-white/10 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-8 space-y-6 scrollbar-hide" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <Bot className="w-16 h-16 text-primary mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-3">Welcome to LEE</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    I'm your AI command system. Tell me what you want to build, analyze, or execute.
                  </p>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 bg-primary flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-primary-foreground" />
                  </div>
                )}
                
                <div
                  className={`max-w-3xl px-6 py-4 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground ml-auto'
                      : 'bg-secondary/50 text-white'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>

                {message.role === 'user' && (
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-4 justify-start">
                <div className="w-10 h-10 bg-primary flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="bg-secondary/50 px-6 py-4">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-6">
            <div className="flex gap-4">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Command LEE..."
                disabled={loading}
                data-testid="chat-input"
                className="flex-1 bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                data-testid="send-message-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AICommand;
